import { useEffect, useState, useRef, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom'
import { IoChevronBackOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import { API_URL } from '../../config/api';
import UndoRedoButtons from './UndoRedoButtons';
import Toolbar from './Toolbar'
import DrawingCanvas from './DrawingCanvas'
import Button from '../Button'
import PromptModal from './PromptModal';
import ColorModal from './ColorModal';
import BrushModal from './BrushModal';
import '../../styles/new-canvas.css'
import { getBrushes } from './brushes';
import { formatError, logError } from "../../services/logging";

const CanvasPage = () => {
    const { promptId } = useParams()
    const [prompt, setPrompt] = useState(null)
    const [isOpen, setIsOpen] = useState(false)
    const [brush, setBrush] = useState({
        type: 'hard',
        size: 10
    })
    const [isBrushOpen, setIsBrushOpen] = useState(false)
    const colorRef = useRef("#000")
    const [colorUI, setColorUI] = useState("#000")
    const [isColorOpen, setIsColorOpen] = useState(false)
    const canvasRef = useRef(null)
    const navigate = useNavigate()

    const brushes = useMemo(() => getBrushes(), [])

    const handleOpen = () => {
        setIsOpen(true)
    }
    
    useEffect(() => {
        const fetchPrompt = async () => {
            try {
                const resp = await fetch(`${API_URL}/api/prompts/${promptId}`)
                if (!resp.ok) {
                    toast.error("Failed to fetch prompt data.")
                    return
                }
                const data = await resp.json()
                setPrompt(data)
            } catch (error) {
                console.error('Error fetching prompt:', error)
            }
        }
        fetchPrompt()
    }, [promptId])

    const handleUndo = () => {
        canvasRef.current?.undo()
    }

    const handleRedo = () => {
        canvasRef.current?.redo()
    }

    const saveArtwork = async () => {
        const stage = canvasRef.current?.getStage()
        if (!stage) {
            toast.warn("No canvas found!")
            return
        }

        try {
            // Convert stage to Blob
            const dataURL = stage.toDataURL({ pixelRatio: 2 })
            const blob = await (await fetch(dataURL)).blob()

            if (!blob) {
                toast.warn("Canvas is empty! Add something before saving.")
                return
            }

            const formData = new FormData()
            formData.append("canvas", blob)
            formData.append("prompt_id", promptId)
            formData.append("folder", "user-images")

            // Upload to Cloudinary
            const uploadResp = await fetch(`${API_URL}/api/upload`, {
                method: "POST",
                body: formData
            })

            if (!uploadResp.ok) {
                const errorData = await uploadResp.json()
                logError(errorData)
                toast.error(errorData.message || "Image upload failed.")
                return
            }

            const imgURL = await uploadResp.text()

            // Save artwork record to DB
            const artworkResp = await fetch(`${API_URL}/api/artworks`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    prompt_id: promptId,
                    image_url: imgURL,
                    image_type: "canvas"
                })
            })

            const data = await artworkResp.json()
            if (!artworkResp.ok) {
                logError(data)
                toast.error(data.message || "Failed to save artwork.")
                return
            }

            toast.success("Artwork saved successfully!")
            navigate(`/artwork/${data.id}`)
        } catch (error) {
            logError(formatError(error))
            toast.error("An unexpected error occurred. Please try again.")
        }
    }

    return (
        <div className='canvas-page'>
            <div className='canvas-header-wrapper'>
                <section className='canvas-header'>
                    <Link to="/prompts" >
                        <h3>
                            <IoChevronBackOutline />
                        </h3>
                    </Link>
                    <h3>Canvas</h3>
                    <h3 
                        className='clickable'
                        onClick={handleOpen}
                    >
                        Prompt
                    </h3>
                </section>
            </div>

            <main className='canvas-area'>
                <UndoRedoButtons 
                    onUndo={handleUndo}
                    onRedo={handleRedo}
                />
                <Toolbar 
                    color={colorUI}
                    onColorClick={() => setIsColorOpen(true)}
                    brush={brush}
                    setBrush={setBrush}
                    onBrushClick={() => setIsBrushOpen(true)}
                />
                <Button 
                    variant="primary" 
                    text='Complete' 
                    handleClick={saveArtwork}
                />
                <DrawingCanvas
                    ref={canvasRef}
                    colorRef={colorRef} 
                    brush={brushes[brush.type]}
                    brushSize={brush.size}
                />
            </main>

            <PromptModal 
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                prompt={prompt}
            />

            <BrushModal
                isOpen={isBrushOpen}
                setIsOpen={setIsBrushOpen}
                brush={brush}
                setBrush={setBrush}
            />

            <ColorModal 
                key={isColorOpen} // Force re-mount to reset internal state
                isOpen={isColorOpen}
                setIsOpen={setIsColorOpen}
                color={colorUI}
                colorRef={colorRef}
                onColorChange={setColorUI}
            />

        </div>
    )
}

export default CanvasPage